/**
 * ticket-assignee.ts — who a ticket is assigned to (owner and eng owner).
 *
 * Owner is the product assignee; eng owner is the engineering assignee. The
 * Assigned-to filter matches either. Keys prefer user id so a rename does not
 * split one person into two dropdown options.
 */

import type { FeatureRequest, RoadmapPerson } from '@/api/endpoints/roadmap';
import { findPersonByLabel, findPersonByUserId } from './person-match';

/** Wire stand-in for tickets with neither owner nor eng owner. */
export const UNASSIGNED_ASSIGNEE_KEY = 'unassigned';

function clean(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}

function emailLocalPart(value: string): string {
  const at = value.indexOf('@');
  return at > 0 ? value.slice(0, at) : value;
}

/** Prefer a name; if the stored value is an email, show the local-part. */
function personDisplayName(raw: string): string {
  const value = raw.trim();
  return value.includes('@') ? emailLocalPart(value) : value;
}

function personKey(userId: number | null, name: string | null | undefined): string | null {
  if (userId != null) return `user:${userId}`;
  const label = clean(name);
  return label ? `name:${label.toLowerCase()}` : null;
}

/**
 * Every assignee identity on the ticket. Unassigned tickets have a single
 * `unassigned` key so the dropdown can pull them up.
 */
export function ticketAssigneeKeys(
  request: Pick<FeatureRequest, 'owner' | 'ownerUserId' | 'engOwner' | 'engOwnerUserId'>,
): string[] {
  const keys = new Set<string>();
  const ownerKey = personKey(request.ownerUserId, request.owner);
  const engKey = personKey(request.engOwnerUserId, request.engOwner);
  if (ownerKey) keys.add(ownerKey);
  if (engKey) keys.add(engKey);
  if (keys.size === 0) keys.add(UNASSIGNED_ASSIGNEE_KEY);
  return [...keys];
}

/** True when any selected assignee identity is on the ticket. Empty = anyone. */
export function matchesTicketAssignees(
  request: Pick<FeatureRequest, 'owner' | 'ownerUserId' | 'engOwner' | 'engOwnerUserId'>,
  assignees: readonly string[] | undefined,
): boolean {
  if (!assignees || assignees.length === 0) return true;
  const keys = ticketAssigneeKeys(request);
  return assignees.some((assignee) => keys.includes(assignee));
}

/** One cell label: owner, or owner / eng when they differ. */
export function ticketAssigneeLabel(
  request: Pick<FeatureRequest, 'owner' | 'engOwner'>,
): string {
  const owner = clean(request.owner);
  const eng = clean(request.engOwner);
  if (owner && eng && owner.toLowerCase() !== eng.toLowerCase()) {
    return `${personDisplayName(owner)} / ${personDisplayName(eng)}`;
  }
  const shown = owner ?? eng;
  return shown ? personDisplayName(shown) : '—';
}

function optionLabel(
  request: Pick<FeatureRequest, 'owner' | 'ownerUserId' | 'engOwner' | 'engOwnerUserId'>,
  key: string,
  people: readonly RoadmapPerson[],
): string {
  if (key === UNASSIGNED_ASSIGNEE_KEY) return 'Unassigned';
  if (key.startsWith('user:')) {
    const userId = Number(key.slice('user:'.length));
    // WS1 person roster wins — the one display name every surface uses (R8.2).
    const person = findPersonByUserId(people, userId);
    if (person) return person.displayName;
    if (request.ownerUserId === userId && clean(request.owner)) {
      return personDisplayName(request.owner.trim());
    }
    if (request.engOwnerUserId === userId && clean(request.engOwner)) {
      return personDisplayName(request.engOwner.trim());
    }
  }
  if (key.startsWith('name:')) {
    const person = findPersonByLabel(people, key.slice('name:'.length));
    if (person) return person.displayName;
  }
  const raw = clean(request.owner) ?? clean(request.engOwner);
  return raw ? personDisplayName(raw) : key;
}

/**
 * Unique assignees on the loaded backlog, for the Assigned-to dropdown.
 * Unassigned sorts last so named people stay easy to scan. When the WS1
 * person roster is provided, labels resolve to canonical display names.
 */
export function collectAssigneeOptions(
  tickets: readonly Pick<
    FeatureRequest,
    'owner' | 'ownerUserId' | 'engOwner' | 'engOwnerUserId'
  >[],
  people: readonly RoadmapPerson[] = [],
): Array<{ value: string; label: string }> {
  const byKey = new Map<string, string>();
  for (const ticket of tickets) {
    for (const key of ticketAssigneeKeys(ticket)) {
      if (!byKey.has(key)) byKey.set(key, optionLabel(ticket, key, people));
    }
  }
  return [...byKey.entries()]
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => {
      const aSpecial = a.value === UNASSIGNED_ASSIGNEE_KEY;
      const bSpecial = b.value === UNASSIGNED_ASSIGNEE_KEY;
      if (aSpecial !== bSpecial) return aSpecial ? 1 : -1;
      return a.label.localeCompare(b.label);
    });
}
